import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Award, Calendar, Star } from 'lucide-react';
import axios from 'axios';
import WeekendHomeCard from '../components/WeekendHomeCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHOW_TESTIMONIALS = false;

const Home = () => {
  const [tours, setTours] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  const whatsappNumber = '917827327243';
  const whatsappMessage = 'Hi! I want to book a tour with Delhi Guided Tours.';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [toursResponse, testimonialsResponse] = await Promise.all([
          axios.get(`${API}/tours`),
          axios.get(`${API}/testimonials`)
        ]);
        setTours(toursResponse.data);
        setTestimonials(testimonialsResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '100px 0', textAlign: 'center' }}>
        <p className="body-large">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section
        style={{
          background: `linear-gradient(rgba(255, 254, 242, 0.9), rgba(255, 254, 242, 0.9)), url('https://images.unsplash.com/photo-1648732788948-a505497d4923?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxkZWxoaSUyMGhlcml0YWdlJTIwbW9udW1lbnRzfGVufDB8fHx8MTc2NTg4MjYwMnww&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '120px 0',
          borderBottom: '1px solid var(--border-light)'
        }}
      >
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h1 className="hero-large" style={{ marginBottom: '24px' }}>
              Explore Delhi Through Curated Heritage Experiences
            </h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '48px' }}>
              Expert-led, small-group tours that bring Delhi's history to life through authentic storytelling and immersive experiences.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/weekend-tour" className="btn-primary" data-testid="home-hero-weekend-tour-btn">
                View the Weekend Tour
                <ArrowRight size={18} />
              </Link>
              <a
                href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Book on WhatsApp
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <WeekendHomeCard />

      {/* Why Choose Us */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '60px' }}>
            Why Delhi Guided Tours
          </h2>
          <div className="grid-three-column">
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '60px', height: '60px', border: '1px solid var(--border-medium)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                <Users size={28} color="var(--text-primary)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>Small Groups</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Small groups for an intimate, personalized experience
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '60px', height: '60px', border: '1px solid var(--border-medium)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                <Award size={28} color="var(--text-primary)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>Expert Guides</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Local historians and storytellers who bring heritage to life
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '60px', height: '60px', border: '1px solid var(--border-medium)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                <Calendar size={28} color="var(--text-primary)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>Fixed Departures</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Weekly scheduled tours - easy booking, guaranteed departures
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Tours */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ marginBottom: '48px', textAlign: 'center' }}>
            <h2 className="heading-1" style={{ marginBottom: '16px' }}>Weekly Departure Tours</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Join our curated small-group heritage walks every weekend
            </p>
          </div>
          <div className="grid-three-column">
            {tours.map((tour) => (
              <div key={tour.tour_id} className="product-card">
                <img src={tour.image} alt={tour.title} className="product-card-image" />
                <div style={{ padding: '24px' }}>
                  <span className="body-small" style={{ color: 'var(--text-light)', display: 'block', marginBottom: '8px' }}>
                    {tour.subtitle}
                  </span>
                  <h3 className="heading-3" style={{ marginBottom: '12px' }}>{tour.title}</h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                    {tour.description}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
                    <span className="heading-3" style={{ color: 'var(--text-primary)' }}>{tour.price}</span>
                    <Link to={`/weekly-tours#${tour.tour_id}`} className="btn-secondary">
                      View Details
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/weekly-tours" className="btn-primary">
              See All Weekly Tours
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {SHOW_TESTIMONIALS && <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '60px' }}>Guest Experiences</h2>
          <div className="grid-three-column">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.testimonial_id}
                style={{
                  padding: '32px',
                  border: '1px solid var(--border-light)',
                  background: 'var(--bg-primary)'
                }}
              >
                <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} fill="var(--text-primary)" color="var(--text-primary)" />
                  ))}
                </div>
                <p className="body-regular" style={{ marginBottom: '24px', color: 'var(--text-primary)' }}>
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="body-small" style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                    {testimonial.name}
                  </p>
                  <p className="body-small" style={{ color: 'var(--text-light)' }}>
                    {testimonial.location}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>}

      {/* CTA Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)', textAlign: 'center' }}>
        <div className="container">
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
              Ready to Explore Delhi's Heritage?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '40px' }}>
              Book your spot on our weekly tours or enquire about private experiences
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/weekly-tours" className="btn-primary">
                Browse Weekly Tours
              </Link>
              <Link to="/contact" className="btn-primary">
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;